import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';

export interface DepartmentDto {
  id?: number;
  code?: string;
  nameTh?: string;
  nameEn?: string;
}

@Component({
  selector: 'app-department-autocomplete',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './department-autocomplete.component.html',
  styleUrl: './department-autocomplete.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DepartmentAutocompleteComponent),
      multi: true
    }
  ]
})
export class DepartmentAutocompleteComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() id = 'department';
  @Input() name = 'department';
  @Input() placeholder = 'พิมพ์ค้นหารหัสหรือชื่อหน่วยงาน';
  @Input() size: 'sm' | 'md' = 'md';
  @Input() bindValue?: keyof DepartmentDto | null = 'id';
  @Input() disabled = false;

  @ViewChild('searchInput', {static: true}) searchInput?: ElementRef<HTMLInputElement>;

  searchText = '';
  suggestions: DepartmentDto[] = [];
  loading = false;
  open = false;
  highlightedIndex = -1;
  readonly standaloneOptions = {standalone: true};

  private selectedValue: DepartmentDto | number | string | null = null;
  private requestSequence = 0;
  private searchSubject$ = new Subject<string>();
  private searchSub?: Subscription;

  private onChange: (value: DepartmentDto | number | string | null) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly http = inject(HttpClient);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.searchSub = this.searchSubject$.pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((term) => {
      this.executeSearch(term);
    });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  writeValue(value: DepartmentDto | number | string | null): void {
    this.selectedValue = value;

    if (this.selectedValue == null || this.selectedValue === '') {
      this.searchText = '';
      this.suggestions = [];
      this.cdr.markForCheck();
      return;
    }

    if (typeof this.selectedValue === 'object') {
      this.searchText = this.formatDepartment(this.selectedValue);
      this.cdr.markForCheck();
      return;
    }

    const numId = Number(this.selectedValue);
    if (this.bindValue === 'id' && !isNaN(numId) && numId > 0) {
      const found = this.suggestions.find((s) => s.id === numId);
      if (found) {
        this.searchText = this.formatDepartment(found);
        this.cdr.markForCheck();
        return;
      }

      this.http.get<DepartmentDto>(`${this.departmentApiUrl()}/${numId}`).subscribe({
        next: (department) => {
          this.searchText = this.formatDepartment(department);
          this.cdr.markForCheck();
        },
        error: () => {
          this.searchText = `${this.selectedValue}`;
          this.cdr.markForCheck();
        }
      });
      return;
    }

    this.searchText = `${this.selectedValue}`;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: DepartmentDto | number | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.highlightedIndex = -1;
      this.onTouched();
      this.cdr.markForCheck();
    }
  }

  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target === this.host.nativeElement) {
      this.searchInput?.nativeElement.focus();
    }
  }

  onInputChange(): void {
    const term = this.searchText.trim();
    this.selectedValue = null;
    this.onChange(null);
    this.highlightedIndex = -1;

    if (!term) {
      this.suggestions = [];
      this.open = false;
      this.loading = false;
      return;
    }

    this.loading = true;
    this.searchSubject$.next(term);
  }

  onFocus(): void {
    if (this.disabled) return;
    if (this.suggestions.length > 0) {
      this.open = true;
    } else if (!this.searchText.trim()) {
      this.loadInitialSuggestions();
    } else {
      this.executeSearch(this.searchText.trim());
    }
  }

  handleBlur(): void {
    // Let onClickOutside manage closing on clicks outside so item selection isn't interrupted
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.open) {
          this.openDropdown();
        } else if (this.suggestions.length > 0) {
          this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestions.length;
          this.scrollHighlightedIntoView();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.open && this.suggestions.length > 0) {
          this.highlightedIndex = (this.highlightedIndex - 1 + this.suggestions.length) % this.suggestions.length;
          this.scrollHighlightedIntoView();
        }
        break;

      case 'Enter':
        if (this.open && this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
          event.preventDefault();
          this.selectDepartment(this.suggestions[this.highlightedIndex]);
        }
        break;

      case 'Escape':
        if (this.open) {
          event.preventDefault();
          event.stopPropagation();
          this.open = false;
          this.highlightedIndex = -1;
        }
        break;

      case 'Tab':
        this.open = false;
        this.highlightedIndex = -1;
        break;
    }
  }

  selectDepartment(item: DepartmentDto, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedValue = item;
    this.searchText = this.formatDepartment(item);
    this.suggestions = [];
    this.open = false;
    this.highlightedIndex = -1;

    let valueToEmit: DepartmentDto | number | string | null = null;
    if (this.bindValue === null || this.bindValue === undefined) {
      valueToEmit = item;
    } else if (this.bindValue === 'id') {
      valueToEmit = item.id ?? null;
    } else {
      valueToEmit = (item as any)[this.bindValue] ?? null;
    }

    this.onChange(valueToEmit);
    this.onTouched();
    this.cdr.markForCheck();
  }

  clear(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.disabled) return;

    this.searchText = '';
    this.selectedValue = null;
    this.suggestions = [];
    this.open = false;
    this.highlightedIndex = -1;
    this.onChange(null);
    this.onTouched();
    this.cdr.markForCheck();
    this.searchInput?.nativeElement.focus();
  }

  toggleOpen(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.disabled) return;

    if (this.open) {
      this.open = false;
      this.highlightedIndex = -1;
    } else {
      this.openDropdown();
    }
    this.cdr.markForCheck();
  }

  isSelected(item: DepartmentDto): boolean {
    if (this.selectedValue == null) return false;
    if (typeof this.selectedValue === 'object') {
      return this.selectedValue.id != null && this.selectedValue.id === item.id;
    }
    if (this.bindValue === 'id') {
      return item.id != null && Number(this.selectedValue) === item.id;
    }
    if (this.bindValue === 'nameTh') {
      return this.selectedValue === item.nameTh;
    }
    return false;
  }

  private openDropdown(): void {
    this.searchInput?.nativeElement.focus();
    if (this.searchText.trim() && this.selectedValue == null) {
      this.executeSearch(this.searchText.trim());
    } else {
      this.loadInitialSuggestions();
    }
  }

  private loadInitialSuggestions(): void {
    const currentSeq = ++this.requestSequence;
    this.loading = true;
    this.http.get<DepartmentDto[]>(this.departmentApiUrl()).subscribe({
      next: (departments) => {
        if (currentSeq !== this.requestSequence) return;
        this.suggestions = departments ?? [];
        this.open = true;
        this.highlightedIndex = -1;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (currentSeq !== this.requestSequence) return;
        this.suggestions = [];
        this.open = false;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private executeSearch(term: string): void {
    const currentSeq = ++this.requestSequence;
    this.loading = true;
    this.http.get<DepartmentDto[]>(`${this.departmentApiUrl()}?search=${encodeURIComponent(term)}`).subscribe({
      next: (departments) => {
        if (currentSeq !== this.requestSequence) return;
        this.suggestions = departments ?? [];
        this.open = true;
        this.highlightedIndex = -1;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (currentSeq !== this.requestSequence) return;
        this.suggestions = [];
        this.open = true;
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private scrollHighlightedIntoView(): void {
    setTimeout(() => {
      const activeEl = this.host.nativeElement.querySelector('.dropdown-item.active') as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  private departmentApiUrl(): string {
    const serverUrl = (environment as any).serverUrl ? (environment as any).serverUrl.replace(/\/+$/, '') : '';
    return `${serverUrl}/api/master/department`;
  }

  private formatDepartment(item: DepartmentDto): string {
    return `${item?.nameTh ?? ''}`.trim();
  }
}
